import { PatientTrackLabel, Track } from '@bariendo/lib/constants';
import { EligibilityIntakeSelections } from '@bariendo/lib/eligibility-intake';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { isEmployerSponsoredTrack } from '@bariendo/lib/pac-inquiry-list';
import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { Patient } from '@medplum/fhirtypes';
import { getEmployer } from '@/services/patient-fields';

/**
 * The PN consultation's key pre-consultation facts: what the chart said going
 * in, not what the call confirms. Procedure and location use the same helpers
 * and source order as the Intake and billing panel's Service and Location rows.
 *
 * `savedAnswers` is the newest *saved* consultation note, never the card's live
 * draft: a fact that changed as the navigator worked the services beat would
 * stop being what was known going in. The facts show only while the note is
 * being written, so this card's own note is never the saved one.
 */

export function likelyProcedure(
  patient: Patient | undefined,
  savedAnswers: PatientAccessCallAnswers | undefined,
  eligibility: Pick<EligibilityIntakeSelections, 'procedure'>
): string | undefined {
  return readInterestedService(patient, savedAnswers, eligibility.procedure);
}

/**
 * The market, with the form's miles only when the market is the form's own
 * inference. Once a rep has set or confirmed it, the distance describes
 * something they did not choose — the same rule as the panel's Location row.
 */
export function likelyLocation(
  patient: Patient | undefined,
  savedAnswers: PatientAccessCallAnswers | undefined,
  eligibility: Pick<EligibilityIntakeSelections, 'market' | 'distanceMi'>
): string | undefined {
  const market = readMarketLocation(patient, savedAnswers, eligibility.market);
  if (!market) return undefined;

  const fromForm = market === eligibility.market && eligibility.distanceMi != null;
  return fromForm ? `${market} · ${eligibility.distanceMi} mi` : market;
}

/**
 * What the card shows when no track is recorded. The Intake and billing panel's
 * Track row words the same state the same way, so the two read alike for one patient.
 */
export const TRACK_NOT_RECORDED = 'Not recorded';

/**
 * The track label, with whoever pays where the chart names them: the employer
 * on an employer-sponsored track, the insurer on the insurance track.
 *
 * Undefined when no track is recorded. Deliberately not self-pay, which is
 * `usePatientTrack`'s default for its other callers: the card shows the Intake
 * and billing panel's Track row state for the same patient instead.
 */
export function likelyTrack(
  track: Track | undefined,
  patient: Patient | undefined,
  insurer: string | undefined
): string | undefined {
  if (!track) return undefined;
  const label = PatientTrackLabel[track];

  const sponsor =
    track === Track.INSURANCE ? insurer : isEmployerSponsoredTrack(track) && patient ? getEmployer(patient) : undefined;

  return sponsor ? `${label} · ${sponsor}` : label;
}
